import { useQuery } from '@tanstack/react-query';
import { API_URL } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface HealthCheck {
  status: 'ok' | 'degraded' | 'down';
  latencyMs?: number;
  error?: string;
}

interface HealthReport {
  status: 'ok' | 'degraded' | 'down';
  uptime: number;
  timestamp: string;
  version: string;
  checks: { db: HealthCheck; redis: HealthCheck };
}

async function fetchHealth(): Promise<HealthReport> {
  const res = await fetch(`${API_URL}/health`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const statusColor: Record<string, string> = {
  ok: 'text-emerald-600',
  degraded: 'text-amber-600',
  down: 'text-red-600',
};

export function HealthPage() {
  const { data, error, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['health'],
    queryFn: fetchHealth,
    refetchInterval: 5_000,
  });

  return (
    <div className="min-h-screen p-8 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-medium">Saúde do sistema</h1>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Atualizando…' : 'Atualizar'}
        </Button>
      </div>

      {isLoading && <div className="text-muted-foreground">Carregando…</div>}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-900 rounded-md p-4">
          Falha ao consultar a API: {(error as Error).message}
        </div>
      )}

      {data && (
        <div className="space-y-4">
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status geral</span>
              <span className={`font-medium uppercase ${statusColor[data.status] ?? ''}`}>
                {data.status}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
              <div>
                <div className="text-muted-foreground">Versão</div>
                <div className="font-mono">{data.version}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Uptime</div>
                <div>{data.uptime}s</div>
              </div>
              <div>
                <div className="text-muted-foreground">Hora</div>
                <div>{new Date(data.timestamp).toLocaleTimeString('pt-BR')}</div>
              </div>
            </div>
          </div>

          {(['db', 'redis'] as const).map((key) => {
            const check = data.checks[key];
            return (
              <div key={key} className="bg-card border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium uppercase text-xs tracking-wider">{key}</span>
                  <span className={`font-medium ${statusColor[check.status] ?? ''}`}>
                    {check.status}
                    {check.latencyMs !== undefined && (
                      <span className="text-muted-foreground font-normal ml-2">
                        {check.latencyMs}ms
                      </span>
                    )}
                  </span>
                </div>
                {check.error && <div className="text-xs text-red-600 mt-2">{check.error}</div>}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 text-center">
        <a href="/login" className="text-sm text-muted-foreground underline">
          ← Voltar ao login
        </a>
      </div>
    </div>
  );
}
